import React, { memo, useCallback, useEffect } from 'react';

import { useFocusEffect } from 'expo-router';
import { useStoreContext } from 'features/store';
import { InteractionManager } from 'react-native';

import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { useShortCodeRefresh } from 'hooks/loyalty/use-short-code-refresh';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useNavigation } from 'navigation/hooks/use-navigation';
import { InRestaurantRedemptionRewardList } from 'pages/loyalty/loyalty-in-restaurant-redemption/reward-redemption-list';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import LoyaltyNotAvailableAtDeliveryMode from 'pages/loyalty/loyalty-not-available-at-delivery-mode';
import LoyaltyNotAvailableAtThisRestaurant from 'pages/loyalty/loyalty-not-available-at-this-restaurant';
import { LoyaltyNotEnrolled } from 'pages/loyalty/loyalty-not-enrolled';
import { LoyaltyButtonArrayWidget } from 'pages/loyalty/loyalty-widgets/loyalty-button-array-widget';
import { LoyaltyPaymentMethodWidget } from 'pages/loyalty/loyalty-widgets/loyalty-payment-method-widget';
import LoyaltyShortCodeWidget from 'pages/loyalty/loyalty-widgets/loyalty-short-code-widget/loyalty-short-code-widget';
import { LoyaltyTitleWidget } from 'pages/loyalty/loyalty-widgets/loyalty-title-widget';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ICartEntryType,
  ShortCodeState,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { IOfferCartIncentive } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart/incentive-cart/types';
import useStaticIdentifier from 'state/loyalty/in-restaurant-redemption/hooks/use-static-identifier';
import { isServiceModeValid } from 'state/loyalty/in-restaurant-redemption/is-service-mode-valid';
import { useServiceModeContext } from 'state/service-mode';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { StyledLoyaltyLoader } from '../loyalty-offers/loyalty-offers.styled';
import {
  LoyaltyButtonWidget,
  LoyaltyImageWidget,
  LoyaltyMarketingTileGroupWidget,
  LoyaltyTextWidget,
} from '../loyalty-widgets';
import { LoyaltyQrCodeLoyaltyIdWidget } from '../loyalty-widgets/loyalty-qr-code-loyalty-id-widget';

import LoyaltyInRestaurantRedemptionView from './loyalty-in-restaurant-redemption.view';
import LoyaltyRedemptionRedesign from './redesign/loyalty-redemption-redesign';
import { LoyaltyInRestaurantRedemptionWidgets } from './types';
import { useFeaturesInRestaurantRedemption } from './use-feature-loyalty-in-restaurant-redemption';
import useWidgetComponents from './use-widget-components';

// @ts-ignore
const componentWidgetMap: Record<LoyaltyInRestaurantRedemptionWidgets, Widget> = {
  [LoyaltyInRestaurantRedemptionWidgets.Button]: LoyaltyButtonWidget,
  [LoyaltyInRestaurantRedemptionWidgets.Image]: LoyaltyImageWidget,
  [LoyaltyInRestaurantRedemptionWidgets.Text]: LoyaltyTextWidget,
  [LoyaltyInRestaurantRedemptionWidgets.MarketingTiles]: LoyaltyMarketingTileGroupWidget,
  [LoyaltyInRestaurantRedemptionWidgets.Title]: LoyaltyTitleWidget,
  [LoyaltyInRestaurantRedemptionWidgets.QrCodeLoyaltyIdWidget]: LoyaltyQrCodeLoyaltyIdWidget,
  [LoyaltyInRestaurantRedemptionWidgets.ShortCodeWidget]: LoyaltyShortCodeWidget,
  [LoyaltyInRestaurantRedemptionWidgets.CartWidget]: InRestaurantRedemptionRewardList,
  [LoyaltyInRestaurantRedemptionWidgets.PaymentMethodWidget]: LoyaltyPaymentMethodWidget,
  [LoyaltyInRestaurantRedemptionWidgets.ButtonArray]: LoyaltyButtonArrayWidget,
};

enum InRestaurantRedemptionStatus {
  unavailableAtDelivery = 'unavailableAtDelivery',
  unavailableAtRestaurant = 'unavailableAtRestaurant',
  notEnrolled = 'notEnrolled',
  noStoreSelected = 'noStoreSelected',
  redemptionDrawer = 'redemptionDrawer',
  redemptionBaseView = 'redemptionBaseView',
  empty = 'empty',
  unavailableOffers = 'unavailableOffers',
}

const LoyaltyInRestaurantRedemption = () => {
  const skipLoyaltyStoreValidation = useFlag(LaunchDarklyFlag.SKIP_LOYALTY_STORE_VALIDATION);
  const enableMyCodeRedesign = useFlag(LaunchDarklyFlag.ENABLE_MY_CODE_REDESIGN);
  const { noStoreSelected } = useStoreContext();
  const { openSignInModal } = useAuthContext();
  const { linkTo } = useNavigation();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const isDesktop = useIsDesktopBreakpoint();

  const {
    inRestaurantRedemptionCart,
    inRestaurantLoyaltyEnabledAtRestaurantLoading,
    inRestaurantLoyaltyEnabledAtRestaurant,
    generateShortCode,
    shortCode,
    shortCodeState,
    shortCodeLoading,
    inRestaurantRedemptionEnabled,
    isInRestaurantRedemptionCartEmpty,
    resetInRestaurantRedemption,
  } = useInRestaurantRedemptionContext();

  useShortCodeRefresh();

  const { isDelivery, serviceMode } = useServiceModeContext();

  const { data } = useFeaturesInRestaurantRedemption();
  const topWidgets = useWidgetComponents(componentWidgetMap, data?.topWidgets);
  const bottomWidgets = useWidgetComponents(componentWidgetMap, data?.bottomWidgets);
  const { isAuthenticated } = useAuthContext();
  const { isStaticIdentifierEnabled } = useStaticIdentifier();

  const { loyaltyUserId } = useLoyaltyUserId();

  const redemptionNotEnabled =
    !inRestaurantRedemptionEnabled ||
    (!skipLoyaltyStoreValidation &&
      !inRestaurantLoyaltyEnabledAtRestaurantLoading &&
      !inRestaurantLoyaltyEnabledAtRestaurant);
  const skipCodeGeneration = isStaticIdentifierEnabled && isInRestaurantRedemptionCartEmpty;

  useEffect(() => {
    if (isDesktop) {
      linkTo(routes.base, { replace: true });
    }
  }, [isDesktop, linkTo]);

  useEffect(() => {
    if (
      loyaltyUserId &&
      !redemptionNotEnabled &&
      !shortCode &&
      shortCodeState === ShortCodeState.None &&
      !shortCodeLoading &&
      !skipCodeGeneration
    ) {
      generateShortCode();
    }
  }, [
    generateShortCode,
    inRestaurantLoyaltyEnabledAtRestaurantLoading,
    loyaltyUserId,
    redemptionNotEnabled,
    shortCode,
    shortCodeLoading,
    shortCodeState,
    skipCodeGeneration,
  ]);

  const unavailableOffers = inRestaurantRedemptionCart
    .filter(
      o => o.type === ICartEntryType.OFFER && !checkLoyaltyOfferAvailability(o.details?.offer)
    )
    // @ts-ignore - we already filter by offer
    .map(offer => offer?.details?.offer) as IOfferCartIncentive[];

  // TODO: Review this logic with the PMs
  // TODO: Extract to pure function and add unit tests
  const getStatus = () => {
    if (isDelivery && !isServiceModeValid({ serviceMode })) {
      return InRestaurantRedemptionStatus.unavailableAtDelivery;
    }

    if (!noStoreSelected && loyaltyUserId && redemptionNotEnabled) {
      return InRestaurantRedemptionStatus.unavailableAtRestaurant;
    }

    if (!isAuthenticated) {
      return InRestaurantRedemptionStatus.notEnrolled;
    }

    if (isAuthenticated && noStoreSelected) {
      return InRestaurantRedemptionStatus.noStoreSelected;
    }

    if (noStoreSelected && !skipLoyaltyStoreValidation) {
      return InRestaurantRedemptionStatus.noStoreSelected;
    }

    if (redemptionNotEnabled) {
      return InRestaurantRedemptionStatus.notEnrolled;
    }

    // TODO: consider adding a LoadingState
    if (!data || inRestaurantLoyaltyEnabledAtRestaurantLoading || !loyaltyUserId) {
      return InRestaurantRedemptionStatus.empty;
    }

    if (unavailableOffers.length) {
      return InRestaurantRedemptionStatus.unavailableOffers;
    }

    return enableMyCodeRedesign
      ? InRestaurantRedemptionStatus.redemptionBaseView
      : InRestaurantRedemptionStatus.unavailableAtRestaurant;
  };

  const status = getStatus();

  useFocusEffect(
    useCallback(() => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'inRestaurantRedemption',
      });

      if (status === InRestaurantRedemptionStatus.notEnrolled) {
        // Fix for iOS: Use InteractionManager to wait for all interactions to complete
        InteractionManager.runAfterInteractions(() => {
          openSignInModal();
        });
      }
    }, [openSignInModal, status])
  );

  // The rendered component should depend only on the status
  // In case we need to add more conditional logic we should do it inside getStatus method
  switch (status) {
    case InRestaurantRedemptionStatus.unavailableAtDelivery:
      return <LoyaltyNotAvailableAtDeliveryMode />;

    case InRestaurantRedemptionStatus.unavailableAtRestaurant:
      return <LoyaltyNotAvailableAtThisRestaurant />;

    case InRestaurantRedemptionStatus.notEnrolled:
      return <LoyaltyNotEnrolled />;

    case InRestaurantRedemptionStatus.redemptionBaseView:
      if (enableMyCodeRedesign) {
        return <LoyaltyRedemptionRedesign />;
      }
      return <LoyaltyInRestaurantRedemptionView widgets={{ topWidgets, bottomWidgets }} />;

    case InRestaurantRedemptionStatus.unavailableOffers:
      return (
        <RemoveUnavailableItemsModal
          onConfirmCb={resetInRestaurantRedemption}
          unavailableOffersInRestaurantRedemption={unavailableOffers}
        />
      );

    case InRestaurantRedemptionStatus.empty:
    default:
      return <StyledLoyaltyLoader />;
  }
};

export default memo(LoyaltyInRestaurantRedemption);
